import { LabelPosition, Size, Switch } from "@next-platform/client-common-spicy-ui";
import { AnalyticsEvents, isAgeVerificationMandatory, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { useState } from "react";

import { AgeVerificationWarningModal } from "@/components";
import { NSFW_TOGGLE_LOCALSTORAGE_KEY } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import useAuth from "@/services/useAuth";
import { getBusinessRouteScreen, isAgeVerificationWarningModalNeeded } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import NsfwPromptModal from "../NsfwPromptModal/NsfwPromptModal";
import NsfwSettingsModal from "./NSFWSettingsModal/NsfwSettingsModal";
import { NsfwToggleProps } from "./types";

export default function NsfwToggle({ showNSFW, isNsfwToggled, setNsfwToggle }: NsfwToggleProps) {
  const [isAgeVerificationVisible, setIsAgeVerificationVisible] = useState(false);
  const { userProfile } = useUserInfo();
  const { pagePath } = useLocalizedLocation();
  const screenName = getBusinessRouteScreen(pagePath);
  const { logEvent } = useQueueLogEvent();
  const [showNSFWSettingsModal, setShowNSFWSettingsModal] = useState(false);
  const [isNsfwPromptModalOpen, setIsNsfwPromptModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const handleNsfwPromptModalToggle = () => {
    setIsNsfwPromptModalOpen((prev) => !prev);
  };

  const onNsfwToggleChange = () => {
    const value = isNsfwToggled;

    const isWarningModalNeeded = isAgeVerificationWarningModalNeeded(userProfile?.age_verification?.status);

    if (!value && isWarningModalNeeded) {
      setIsAgeVerificationVisible(true);
      return;
    }

    setNsfwToggle(!value);
    typedLocalStorageService.setItem(NSFW_TOGGLE_LOCALSTORAGE_KEY, String(!value));
    logEvent(AnalyticsEvents.SWITCH_NSFW, {
      enable_nsfw: !value,
      page_name: screenName,
    });
  };

  const handleNSFWToggleClick = () => {
    if (!isAuthenticated) {
      return handleNsfwPromptModalToggle();
    }
    if (!showNSFW) {
      return setShowNSFWSettingsModal(true);
    }
    return onNsfwToggleChange();
  };

  const onCloseAgeVerificationWarningModal = () => {
    setIsAgeVerificationVisible(false);
    if (showNSFW && isAgeVerificationMandatory(userProfile?.age_verification?.status)) {
      // Age verification is mandatory, so we can turn NSFW off
      setNsfwToggle(false);
      typedLocalStorageService.setItem(NSFW_TOGGLE_LOCALSTORAGE_KEY, "false");
    } else {
      // Age verification is optional, so we can turn NSFW on
      setNsfwToggle(true);
      typedLocalStorageService.setItem(NSFW_TOGGLE_LOCALSTORAGE_KEY, "true");
    }
  };

  return (
    <>
      <Switch
        checked={isNsfwToggled}
        onCheckedChange={handleNSFWToggleClick}
        label={t("common:NSFW")}
        labelPosition={LabelPosition.END}
        size={Size.MEDIUM}
        data-testid="NsfwToggle"
      />

      <NsfwPromptModal isOpen={isNsfwPromptModalOpen} onClose={handleNsfwPromptModalToggle} />

      <NsfwSettingsModal showModal={showNSFWSettingsModal} setShowModal={setShowNSFWSettingsModal} />

      <AgeVerificationWarningModal showNSFW={isNsfwToggled} isOpen={isAgeVerificationVisible} onClose={onCloseAgeVerificationWarningModal} />
    </>
  );
}
